'use client';

import { c as _c } from "react/compiler-runtime";
import { InfoTooltip } from '@morpho-org/ui-v6/client';
import { ExternalLink, Skeleton } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { InterestGenerated, Rewards } from '@repo/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { memo } from 'react';
import type { CardNetApyVariant } from '../CardNetApy';
import { CardNetApyHeaderWrapper } from '../CardNetApyParts';
import './CardNetApyHeader.css';

/* eslint-disable better-tailwindcss/no-unregistered-classes */

export type ModeCard = 'apy' | 'reward';
export interface CardNetApyHeaderProps {
  value: string;
  isExpanded?: boolean;
  variant: CardNetApyVariant;
  mode: ModeCard;
  hasRewardClaimable?: boolean;
  loading?: boolean;
  onModeChange: (mode: ModeCard) => void;
}
const textCardApy = {
  vault: {
    apy: {
      title: 'Net APY',
      tooltip: "The net APY you're earning across all Morpho Vaults.",
      tooltipButton: 'View your Net APY.'
    },
    reward: {
      title: 'Claimable Rewards',
      tooltip: 'Rewards that you can claim from Morpho.'
    }
  },
  market: {
    apy: {
      title: 'Net Rate',
      tooltip: "The net rate you're borrowing across all Morpho Markets.",
      tooltipButton: 'View your Net Rate.'
    },
    reward: {
      title: 'Claimable Rewards',
      tooltip: 'Rewards that you can claim from Morpho.'
    }
  }
};
const CardNetApyHeader = t0 => {
  const $ = _c(65);
  const {
    value,
    isExpanded,
    variant,
    mode,
    onModeChange,
    hasRewardClaimable,
    loading
  } = t0;
  const tooltipContent = textCardApy[variant][mode].tooltip;
  const title = textCardApy[variant][mode].title;
  const tooltipButton = textCardApy[variant].apy.tooltipButton;
  let t1;
  if ($[0] !== title) {
    t1 = <TooltipTrigger asChild={true}><span className="text-2xs text-secondary-foreground whitespace-nowrap max-sm:text-[13px]">{title}</span></TooltipTrigger>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== tooltipContent) {
    t2 = !!tooltipContent && <TooltipContent>{tooltipContent}</TooltipContent>;
    $[2] = tooltipContent;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== mode) {
    t4 = mode === "reward" && <InfoTooltip disableHoverableContent={false} tooltipContent={<span>Merkl rewards distributed by Morpho must be claimed within 6 months of a campaign ending.{" "}<ExternalLink href="https://docs.morpho.org/learn/concepts/rewards/#distribution-system" style={{
        textDecoration: "underline"
      }}>Learn more</ExternalLink></span>} />;
    $[7] = mode;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="gap-xs flex h-4 flex-row items-center">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== loading || $[13] !== mode || $[14] !== value) {
    t6 = loading && mode === "reward" ? <Skeleton className="h-[24px] w-[72px] rounded-[20px]" /> : <div className="flex gap-[1px]">{mode === "reward" && value !== "-" && <span className="text-title-xs text-muted-foreground">$</span>}<span className="text-title-xs text-foreground">{value}</span>{mode === "apy" && <span className="text-title-xs text-muted-foreground">%</span>}</div>;
    $[12] = loading;
    $[13] = mode;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="gap-xs flex flex-col items-start">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0
    };
    t9 = {
      opacity: 1
    };
    t10 = {
      opacity: 0
    };
    t11 = {
      duration: 0.1
    };
    $[19] = t10;
    $[20] = t11;
    $[21] = t8;
    $[22] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  const t12 = mode === "apy" ? "secondary" : "ghost";
  let t13;
  if ($[23] !== onModeChange) {
    t13 = () => onModeChange("apy");
    $[23] = onModeChange;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = mode === "apy" && "dark:border-transparent dark:bg-secondary dark:shadow-none";
  let t15;
  if ($[25] !== t14) {
    t15 = cn("transition-none", t14);
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <InterestGenerated />;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t15) {
    t17 = <TooltipTrigger asChild={true}><motion.div key="apy-button" initial={t8} animate={t9} exit={t10} transition={t11}><Button variant={t12} size="sm" onClick={t13} className={t15}>{t16}APY</Button></motion.div></TooltipTrigger>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t15;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== tooltipButton) {
    t18 = <TooltipContent>{tooltipButton}</TooltipContent>;
    $[32] = tooltipButton;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t17 || $[35] !== t18) {
    t19 = <AnimatePresence mode="wait" initial={false}><Tooltip>{t17}{t18}</Tooltip></AnimatePresence>;
    $[34] = t17;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== hasRewardClaimable) {
    t20 = hasRewardClaimable && <div className="bg-info-foreground card-net-apy-badge-pop-up-scale absolute top-px right-px flex size-2 items-center justify-center rounded-full text-center" />;
    $[37] = hasRewardClaimable;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      opacity: 0
    };
    t22 = {
      opacity: 1
    };
    t23 = {
      opacity: 0
    };
    t24 = {
      duration: 0.1
    };
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
    $[42] = t24;
  } else {
    t21 = $[39];
    t22 = $[40];
    t23 = $[41];
    t24 = $[42];
  }
  const t25 = mode === "reward" ? "secondary" : "ghost";
  let t26;
  if ($[43] !== onModeChange) {
    t26 = () => onModeChange("reward");
    $[43] = onModeChange;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  const t27 = mode === "reward" && "dark:border-transparent dark:bg-secondary dark:shadow-none";
  let t28;
  if ($[45] !== t27) {
    t28 = cn("transition-none", t27);
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Rewards />;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== t25 || $[49] !== t26 || $[50] !== t28) {
    t30 = <TooltipTrigger asChild={true}><motion.div key="reward-button" initial={t21} animate={t22} exit={t23} transition={t24}><Button data-testid="reward-button" size="sm" variant={t25} onClick={t26} className={t28}>{t29}Rewards</Button></motion.div></TooltipTrigger>;
    $[48] = t25;
    $[49] = t26;
    $[50] = t28;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <TooltipContent>View and claim your rewards.</TooltipContent>;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== t30) {
    t32 = <AnimatePresence mode="wait" initial={false}><Tooltip>{t30}{t31}</Tooltip></AnimatePresence>;
    $[53] = t30;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== t20 || $[56] !== t32) {
    t33 = <div className="h-lg relative flex items-center">{t20}{t32}</div>;
    $[55] = t20;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t19 || $[59] !== t33) {
    t34 = <div className="gap-xs flex items-center">{t19}{t33}</div>;
    $[58] = t19;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== isExpanded || $[62] !== t34 || $[63] !== t7) {
    t35 = <CardNetApyHeaderWrapper isExpanded={isExpanded}>{t7}{t34}</CardNetApyHeaderWrapper>;
    $[61] = isExpanded;
    $[62] = t34;
    $[63] = t7;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  return t35;
};
export default memo(CardNetApyHeader);